import React, { useCallback } from 'react';
import { CommentList } from 'lib/comments-common';
import { hasUnreadMentionsForUser } from './helper';
import { useSelector } from 'react-redux';
import { selectUserProfile } from 'app/pages/Layout/slice/selectors';
import { selectIsLoading } from './CommentState/selectors';
import { useFilteredComments } from './hooks/useFilteredComments';
import { useLoadMoreScrollToComment } from './hooks/useLoadMoreScrollToComment';
import { useSubmitHandlers } from './hooks/useSubmitHandlers';
import { useEditHandlers } from './hooks/useEditHandlers';
import { useMenuActionHandlers } from './hooks/useMenuActionHandlers';
import { useReplyMenuActionHandlers } from './hooks/useReplyMenuActionHandlers';
import { useSlideoutUrl } from '../../../hooks/useSlideoutUrl';
import { useDispatch } from 'react-redux';
import { useCommentSlice } from './CommentState';
import { generateTimestamp } from './helper';
import { DeleteConfirmationDialog } from '../../shared/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useRBAC } from '../context/RBACContext';
import { MenuTriggerIcon } from '../../Icons/MenuTriggerIcon';

// Define UserRole type locally since it's not exported from the package
type UserRole = 'ADMIN' | 'RESOLVER' | 'VIEWER';

const CommentListContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { actions } = useCommentSlice();
  const loggedInUser = useSelector(selectUserProfile);
  const isLoading = useSelector(selectIsLoading);
  const filteredComments = useFilteredComments();
  const { currentCommentId } = useSlideoutUrl();

  const { handleReplySubmit } = useSubmitHandlers();
  const { handleEdit } = useEditHandlers();
  const { handleMenuAction, deleteDialog } = useMenuActionHandlers();
  const { handleReplyMenuAction, replyDeleteDialog } =
    useReplyMenuActionHandlers();

  // Get permissions from RBAC context
  const { canEdit, isAdmin } = useRBAC();

  const customMentionRenderer = useCallback((user: any) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontWeight: 500 }}>{user.name}</span>
        <span style={{ color: '#666', fontSize: '0.9em' }}>
          ({user.username})
        </span>
      </div>
    );
  }, []);

  // Use the scroll to comment hook
  // useScrollToComment({
  //   commentId: currentCommentId,
  //   filteredComments: filteredComments
  // });

  const { listContainerRef, found, pageStart } = useLoadMoreScrollToComment({
    commentId: currentCommentId,
    comments: filteredComments,
    pageSize: 10 // It should be same as initialVisiblecount if any
  });

  const handleCommentClick = (commentId: string) => {
    // Find the clicked comment
    const clickedComment = filteredComments.find(c => c.id === commentId);
    if (!clickedComment || clickedComment.commentStatus === 'RESOLVED') return;
    const has = hasUnreadMentionsForUser(clickedComment, loggedInUser.email);
    // Check if this comment has unread mentions for the logged-in user
    if (has) {
      dispatch(
        actions.markAsRead({
          commentId: commentId,
          uid: loggedInUser.uid,
          markAsRead: true,
          timestamp: generateTimestamp()
        })
      );
    }
  };

  // Determine user role based on RBAC permissions
  // ADMIN role: Only for users with isAdmin permission (full access to all comments)
  // RESOLVER role: For users with canEdit permission (can resolve/unresolve any comment, but only edit/delete their own)
  // undefined: When neither permission is set, use default author-based permissions (users can only manage their own comments)
  const getUserRole = (): UserRole | undefined => {
    if (isAdmin) {
      return 'ADMIN';
    }
    if (canEdit) {
      return 'RESOLVER';
    }
    return undefined;
  };

  return (
    <>
      <div ref={listContainerRef}>
        <CommentList
          isLoading={isLoading}
          comments={filteredComments}
          loggedInuserEmail={loggedInUser.email}
          userRole={getUserRole()}
          showMenu={true}
          allowReplies={true}
          useVirtualization={{
            type: 'LOAD_MORE',
            initialStartIndex: found ? pageStart : undefined
          }}
          // Enhanced menu options with all features
          menuOptions={['Edit', 'Delete', 'Resolve']}
          replyMenuOptions={['Edit', 'Delete']}
          // Event handlers
          handleEdit={handleEdit}
          handleMenuAction={handleMenuAction}
          handleReplyMenuAction={handleReplyMenuAction}
          handleReplySubmit={handleReplySubmit}
          styleOverrides={{
            padding: 0
          }}
          commentOnClickHandler={handleCommentClick}
          inputConfig={
            {
              maxSuggestions: 50, // Increase from default 10 to 20
              usersApiEndpoint: `${process.env.REACT_APP_API_V2_URL}/ldap/users`,
              mentionPopupDisplay: customMentionRenderer
            } as any
          }
          iconOverrides={{
            pencilIcon: null,
            pencilIconStyles: { display: 'none' },
            menuTriggerIcon: <MenuTriggerIcon />
          }}
        />
      </div>

      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Comment?"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        onConfirm={deleteDialog.onConfirm}
        onCancel={deleteDialog.onCancel}
      />

      <DeleteConfirmationDialog
        isOpen={replyDeleteDialog.isOpen}
        title="Delete Reply?"
        message="Are you sure you want to delete this reply? This action cannot be undone."
        onConfirm={replyDeleteDialog.onConfirm}
        onCancel={replyDeleteDialog.onCancel}
      />
    </>
  );
};

export default CommentListContainer;
